import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { of, throwError } from 'rxjs';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import {catchError, map, tap} from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import {environment} from "../../environments/environment";
import {OrderDeliverTo} from "../../model/OrderDeliverTo";
import {DataService} from "./data-service";
import {isErrorBody, retryTransient, toServerCallError} from "./http-error";
import {LanguagePipe} from "../pipe/language.pipe";

@Injectable()
export class OrderDeliverToService extends DataService {
  private objects: OrderDeliverTo[] = [];
  private objectsObservable = of( this.objects );
  public loadingState = 'INITIAL';


  constructor(private http: HttpClient, private messageService: MessageService, private startupService: StartupService) {
    super(startupService);
  }

    reset (filter: string = '') {
    this.loadingState = 'INITIAL';
    while ( this.objects.length > 0) {
      this.objects.pop();
    }
  }

  getOrderDeliverTos(): Observable<OrderDeliverTo[]> {
    if ( this.loadingState === 'INITIAL') {
      this.loadingState = 'LOADING';
      const url = `${environment.apiBaseURL}orderDeliverTo`;
      this.messageService.add('OrderDeliverToService: fetched orderDeliverTos');
      return this.http.get<OrderDeliverTo[]>(url, this.startupService.getHttpOptions())
        .pipe(
          map(orderDeliverTos => {
            while ( this.objects.length > 0) {
              this.objects.pop();
            }
            for ( const orderDeliverTo of orderDeliverTos ) {
              const o = new OrderDeliverTo(orderDeliverTo);
              this.objects.push(o);
            }
            this.loadingState = 'LOADED';
            return this.objects;
          }), catchError(this.handleError('getOrderDeliverTos', []))
        );
    } else {
      return this.objectsObservable;
    }
  }


  getOrderDeliverTo(id: string): Observable<OrderDeliverTo> {
    const url = `${environment.apiBaseURL}orderDeliverTo/${id}`;
    this.messageService.add(`OrderDeliverToService: fetched orderDeliverTo id=${id}`);
    return this.http.get<OrderDeliverTo>(url, this.startupService.getHttpOptions())
      .pipe(
        map(orderDeliverTo => {
          const p = new OrderDeliverTo(orderDeliverTo);
          return p;
        }),
        catchError(this.handleError<OrderDeliverTo>(`getOrderDeliverTo id=${id}`))
    );
  }

  /** PUT: update the orderDeliverTo on the server */
  updateOrderDeliverTo (orderDeliverTo: OrderDeliverTo): Observable<OrderDeliverTo> {
    const url = `${environment.apiBaseURL}orderDeliverTo/${orderDeliverTo.ID}`;
    this.messageService.add(`OrderDeliverToService: updated orderDeliverTo id=${orderDeliverTo.ID}`);
    return this.http.put(url, orderDeliverTo, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`updated orderDeliverTo id=${orderDeliverTo.ID}`)),
      catchError(this.handleError<any>('updateOrderDeliverTo'))
    );
  }

  /**
   * Create an OrderDeliverTo (and, server-side, the Order rows derived from
   * it). **This method reports failure by ERRORING, not by emitting
   * `undefined`** -- unlike every other method on this service. Callers must
   * supply an error handler; `subscribe(next)` alone will produce an unhandled
   * rejection rather than silently continuing.
   *
   * That asymmetry is deliberate and is the fix for PF-13. Two live callers
   * decide something irreversible on the strength of this result --
   * `CartPageComponent` clears the customer's cart, and
   * `ManufacturingBucketsComponent` reports a replenishment as placed -- and
   * `of(undefined)` is indistinguishable at the call site from "the server
   * said no, and here is why". The reason has to survive the round trip.
   *
   * Three changes from the original, each guarding a different failure:
   *
   *   1. `retry(6)` -> `retryTransient(1, 500)`. A 400 is a decision, not a
   *      glitch; re-sending it seven times just delayed the message. One
   *      retry, transient failures only. See the hazard note on
   *      {@link retryTransient} -- POST is not idempotent, so the count is
   *      deliberately small.
   *   2. A success status carrying an error body is treated as a failure
   *      ({@link isErrorBody}). This is what PF-13 actually was: a SmartError
   *      served with 201, wrapped by `new OrderDeliverTo(...)` into a truthy
   *      object that passed every `if`. The server no longer does it; the
   *      guard stays because the symptom was a cleared cart and the cost is
   *      one property test.
   *   3. The local cache is only appended to on a genuine create. The old
   *      code pushed whatever came back, so a failure body ended up in
   *      `this.objects` masquerading as a saved row.
   */
  addOrderDeliverTo (orderDeliverTo: OrderDeliverTo): Observable<OrderDeliverTo> {
    const url = `${environment.apiBaseURL}orderDeliverTo`;
    return this.http.post<OrderDeliverTo>(url, orderDeliverTo, this.startupService.getHttpOptions()).pipe(
      retryTransient<OrderDeliverTo>(1, 500),
      map(o => {
        if ( isErrorBody(o) || !(o as any).ID ) {
          throw toServerCallError(
            { status: 201, error: o },
            'addOrderDeliverTo',
            LanguagePipe.get('The order could not be created.')
          );
        }
        this.log(`added orderDeliverTo w/ id=${(o as any).ID}`);
        const x = new OrderDeliverTo(o);
        this.objects.push(x);
        return x;
     }),
      catchError(error => {
        this.loadingState = 'INITIAL';
        const wrapped = toServerCallError(error, 'addOrderDeliverTo',
          LanguagePipe.get('The order could not be created.'));
        console.error('addOrderDeliverTo failed', wrapped.statusCode, wrapped.code, wrapped.details, error);
        this.log(`addOrderDeliverTo failed: ${wrapped.message}`);
        return throwError(wrapped);
      })
    );
  }

  /** DELETE: delete the orderDeliverTo from the server */
  deleteOrderDeliverTo (orderDeliverTo: OrderDeliverTo): Observable<OrderDeliverTo> {
    const url = `${environment.apiBaseURL}orderDeliverTo/${orderDeliverTo.ID}`;

    return this.http.delete<OrderDeliverTo>(url, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`deleted orderDeliverTo id=${orderDeliverTo.ID}`)),
      catchError(this.handleError<OrderDeliverTo>('deleteOrderDeliverTo'))
    );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
  private log(message: string) {
    this.messageService.add('OrderDeliverToService: ' + message);
  }

}
